import { db } from '../firebaseConfig';
import { doc, setDoc, serverTimestamp, increment, arrayUnion } from 'firebase/firestore';
import { APP_VERSION } from '../version';

/**
 * errorService.js
 * Captura erros não tratados no app e envia para o Firestore de forma AGRUPADA
 * para minimizar o inchaço do banco e facilitar a auditoria.
 */

class ErrorService {
    constructor() {
        this.initialized = false;
        this.userId = null;
        this.userName = null;
        this.currentPage = 'dashboard';
        this.breadcrumbs = [];
        this.DEDUPLICATION_TIME = 5 * 60 * 1000; // Reduzido para 5min para maior precisão de contagem
    }

    init(userId = null, userName = null) {
        if (this.initialized) return;
        this.userId = userId;
        this.userName = userName;

        // Captura erros globais de JS
        window.onerror = (message, source, lineno, colno, error) => {
            const msg = message?.toString() || "";
            
            // Lógica de Recuperação Automática para erros de carregamento de bundle (404/HTML no JS)
            const isChunkError = msg.toLowerCase().includes("unexpected token '<'") || 
                                 msg.toLowerCase().includes("loading chunk") || 
                                 msg.toLowerCase().includes("load chunk") ||
                                 msg.toLowerCase().includes("syntaxerror: unexpected token") ||
                                 msg.toLowerCase().includes("failed to fetch dynamically imported module");

            if (isChunkError) {
                const lastRetry = sessionStorage.getItem('last_chunk_retry');
                const now = Date.now();
                
                if (!lastRetry || (now - parseInt(lastRetry)) > 10000) {
                    sessionStorage.setItem('last_chunk_retry', now.toString());
                    console.warn("ERROR_SERVICE: Detectada falha de bundle global. Recarregando...");
                    window.location.reload();
                }
                return true; // Suprime o erro pois vamos recarregar ou já tentamos recentemente
            }

            this.reportError({
                type: 'JS_ERROR',
                message: msg,
                source,
                lineno,
                colno,
                stack: error?.stack,
                userAgent: navigator.userAgent,
                url: window.location.href
            });
        };

        // Captura promessas rejeitadas não tratadas
        window.onunhandledrejection = (event) => {
            this.reportError({
                type: 'UNHANDLED_PROMISE',
                reason: event.reason?.message || event.reason,
                stack: event.reason?.stack,
                userAgent: navigator.userAgent,
                url: window.location.href
            });
        };

        this.initialized = true;
        console.log('ERROR_SERVICE: Monitoramento de erros iniciado (Modo Agrupado).');
    }

    setUserId(userId) {
        this.userId = userId;
        this.addBreadcrumb('set_user', { userId });
    }

    setUserName(userName) {
        this.userName = userName;
    }

    setPage(pageName) {
        this.currentPage = pageName || 'dashboard';
        this.addBreadcrumb('navigation', { page: this.currentPage });
    }

    addBreadcrumb(action, details = {}) {
        this.breadcrumbs.push({
            action,
            timestamp: new Date().toISOString(),
            ...details
        });
        if (this.breadcrumbs.length > 5) {
            this.breadcrumbs.shift();
        }
    }

    /**
     * Gera um Hash Único para identificar erros idênticos
     */
    getHash(errorData) {
        try {
            const type = errorData.type || 'UNKNOWN';
            const msg = errorData.message || errorData.reason || 'no-message';
            const stack = (errorData.stack || "").split('\n')[0]; // Linha 1 do stack é a mais relevante
            const platform = this.getPlatform();
            
            // Cria string base para o hash
            const base = `${type}|${msg}|${stack}|${platform}`.substring(0, 300);
            
            // Simples hash numérico convertido para string base64 p/ ID amigável no Firestore
            let hash = 0;
            for (let i = 0; i < base.length; i++) {
                const char = base.charCodeAt(i);
                hash = ((hash << 5) - hash) + char;
                hash = hash & hash; // Convert to 32bit integer
            }
            return `err_${Math.abs(hash).toString(36)}`;
        } catch (e) {
            return `err_fallback_${Date.now()}`;
        }
    }

    getPlatform() {
        if (window.location.hostname.includes('localhost')) return 'dev';
        // Apps Capacitor costumam usar protocolo 'capacitor://' ou 'http://localhost'
        if (window.location.protocol.includes('capacitor') || (window.location.hostname === 'localhost' && !window.location.port)) return 'app';
        return 'web';
    }

    /**
     * Verifica se o mesmo erro já foi enviado por este cliente nos últimos 5 minutos
     */
    isDuplicate(hash) {
        try {
            const key = `err_log_${hash}`;
            const lastSent = sessionStorage.getItem(key);
            const now = Date.now();

            if (lastSent && (now - parseInt(lastSent)) < this.DEDUPLICATION_TIME) {
                return true;
            }

            sessionStorage.setItem(key, now.toString());
            return false;
        } catch (e) {
            return false;
        }
    }

    async reportError(errorData) {
        try {
            // Evita logs infinitos se o próprio reporte falhar
            if (errorData.source?.includes('errorService')) return;

            const hash = this.getHash(errorData);

            // Throttling local para não sobrecarregar o Firestore com escritas do mesmo cliente
            if (this.isDuplicate(hash)) {
                return;
            }

            let logName = this.userName || 'Anônimo';
            if (logName === 'Anônimo' && this.userId) {
                logName = `ID: ${this.userId.substring(0, 6)}...`;
            }

            const platform = this.getPlatform();
            const logRef = doc(db, 'admin_logs', 'errors', 'items', hash);

            // Agrupa usando setDoc com incremento
            await setDoc(logRef, {
                ...errorData,
                appVersion: APP_VERSION,
                breadcrumbs: this.breadcrumbs,
                count: increment(1),
                lastSeen: serverTimestamp(),
                firstSeen: serverTimestamp(), // O merge preserve a data original se já existir
                resolved: false,
                platform: platform,
                page: this.currentPage,
                // Mantém apenas o último usuário afetado para evitar inchaço do documento (limite de 1MB)
                lastAffectedUser: {
                    id: this.userId || 'anonymous',
                    name: logName,
                    at: new Date().toISOString()
                }
            }, { merge: true });

        } catch (err) {
            console.warn('ERROR_SERVICE: Falha silenciosa no reporte:', err);
        }
    }

    /**
     * Reporte manual para blocos try-catch importantes
     */
    log(message, context = {}) {
        this.reportError({
            type: 'MANUAL_LOG',
            message,
            ...context
        });
    }
}

const errorService = new ErrorService();
export default errorService;

